import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Stack,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Box,
  IconButton,
  Tab,
  Tabs,
  Autocomplete,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  FormHelperText,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { Delete, Edit, History, Add } from '@mui/icons-material';
import {
  deleteEntity,
  Entity,
  getEntities,
  updateEntity,
  createEntity,
  CreateEntityRequest,
  getAvailableDataProviders,
} from '../../services/entityService';
import { getEntityBalanceHistory } from '../../services/euaBalanceHistoryService';
import { EuaBalanceHistory } from '../../components/EuaBalanceHistory';
import { UserService, UserInfo } from '../../services/userService';
import { CreateEntityDialog } from './CreateEntityDialog';
import { ToastService } from '../../services/toastService';

export default function EntitiesPage() {
  const queryClient = useQueryClient();
  const [mainTab, setMainTab] = useState(0);
  const [selectedEntity, setSelectedEntity] = useState<Entity | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['entities'],
    queryFn: () => getEntities(), // Don't pass effectiveUserId - always fetch all entities
  });

  // Handle query errors with toast notifications
  useEffect(() => {
    if (isError && error) {
      console.error('Failed to load entities:', error);
      ToastService.handleApiError(error, 'Loading Entities');
    }
  }, [isError, error]);

  // Fetch users for the edit form
  const { data: usersData } = useQuery({
    queryKey: ['users', 'entities'],
    queryFn: UserService.getAllUsers,
    staleTime: 60_000,
  });

  const users: UserInfo[] = useMemo(() => usersData?.users || [], [usersData]);

  const { data: providersData } = useQuery({
    queryKey: ['available-data-providers'],
    queryFn: getAvailableDataProviders,
    staleTime: 60_000,
  });

  const availableProviders = providersData?.providers || [];

  // Email validation function
  const validateEmail = (email: string): string => {
    if (!email) return '';
    const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
    return emailRegex.test(email) ? '' : 'Invalid email address';
  };

  // Validates a comma-separated list of email addresses.
  const validateEmails = (input: string): string => {
    if (!input) return '';
    const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
    const addresses = input
      .split(',')
      .map((e) => e.trim())
      .filter(Boolean);
    const invalid = addresses.filter((e) => !emailRegex.test(e));
    if (invalid.length > 0) return `Invalid email: ${invalid[0]}`;
    return '';
  };

  // Fetch balance history for selected entity
  const { data: balanceHistory = [] } = useQuery({
    queryKey: ['entity-balance-history', selectedEntity?.ID],
    queryFn: () => (selectedEntity ? getEntityBalanceHistory(selectedEntity.ID, 100) : []),
    enabled: !!selectedEntity?.ID,
  });

  const [editing, setEditing] = useState<Entity | null>(null);
  const [form, setForm] = useState<Partial<Entity>>({});
  const [emailError, setEmailError] = useState<string>('');
  const [reportEmailError, setReportEmailError] = useState<string>('');

  useEffect(() => {
    if (editing) {
      setForm({
        AccountName: editing.AccountName,
        MohaAccountNumber: editing.MohaAccountNumber,
        MohaEuaBalance: editing.MohaEuaBalance,
        TraderName: editing.TraderName,
        TraderEmail: editing.TraderEmail,
        ReportEmail: editing.ReportEmail,
        UserId: editing.UserId,
        DataProviders:
          editing.DataProviders && editing.DataProviders.length > 0
            ? editing.DataProviders
            : ['SignalOcean'], // Default to Signal Ocean if no providers
        // Tri-state: undefined (unset) resolves to ON, matching the backend *bool default.
        FuelEUOnReports: editing.FuelEUOnReports ?? true,
      });
      setEmailError(validateEmail(editing.TraderEmail || ''));
      setReportEmailError(validateEmails(editing.ReportEmail || ''));
    }
  }, [editing]);

  // Create entity mutation
  const createMut = useMutation({
    mutationFn: (payload: CreateEntityRequest) => createEntity(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['entities'] });
      // Invalidate dashboard stats since it shows entity counts
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
      setCreateDialogOpen(false);
      ToastService.success('Entity created successfully', 'Entity Created');
    },
    onError: (error: Error) => {
      console.error('Failed to create entity:', error);
      ToastService.handleApiError(error, 'Entity Creation');
    },
  });

  const updateMut = useMutation({
    mutationFn: (vars: { id: string; payload: Partial<Entity> }) =>
      updateEntity(vars.id, vars.payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['entities'] });
      queryClient.invalidateQueries({ queryKey: ['entity-balance-history'] });
      // Invalidate dashboard stats since it shows entity counts
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
      setEditing(null);
      ToastService.success('Entity updated successfully', 'Entity Updated');
    },
    onError: (error: Error) => {
      console.error('Failed to update entity:', error);
      ToastService.handleApiError(error, 'Entity Update');
    },
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteEntity(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['entities'] });
      // Invalidate dashboard stats since it shows entity counts
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
      ToastService.success('Entity deleted successfully', 'Entity Deleted');
    },
    onError: (error: Error) => {
      console.error('Failed to delete entity:', error);
      ToastService.handleApiError(error, 'Entity Deletion');
    },
  });

  const handleCreateEntity = (payload: CreateEntityRequest) => {
    createMut.mutate(payload);
  };

  const cols: GridColDef[] = useMemo(
    () => [
      { field: 'ID', headerName: 'ID', width: 220 },
      {
        field: 'UserId',
        headerName: 'Assigned User',
        width: 250,
        renderCell: (params: GridRenderCellParams<Entity>) => {
          const user = users.find((u) => u.uid === params.value);
          return user ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 0.1,
              }}
            >
              <Typography variant="body2">{UserService.formatDisplayName(user)}</Typography>
              <Typography variant="caption" color="text.secondary">
                {user.email}
              </Typography>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {params.value || 'Unassigned'}
            </Typography>
          );
        },
      },
      { field: 'AccountName', headerName: 'Account', flex: 1 },
      { field: 'MohaAccountNumber', headerName: 'MOHA Account', width: 180 },
      { field: 'MohaEuaBalance', headerName: 'EUA Balance', width: 150, type: 'number' },
      { field: 'TraderName', headerName: 'Trader Name', width: 200 },
      { field: 'TraderEmail', headerName: 'Trader Email', width: 250 },
      { field: 'ReportEmail', headerName: 'Report Email', width: 250 },
      {
        field: 'LinkedUsers',
        headerName: 'Linked Users',
        flex: 1,
        valueGetter: (_value, row: Entity) => {
          return Array.isArray(row?.LinkedUsers) ? row.LinkedUsers.join(', ') : '';
        },
      },
      {
        field: 'DataProviders',
        headerName: 'Data Providers',
        width: 200,
        renderCell: (params: GridRenderCellParams<Entity>) => {
          const providers = params.row.DataProviders || [];
          const displayProviders = providers.length > 0 ? providers : ['SignalOcean']; // Show default if empty
          return (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {displayProviders.map((provider) => (
                <Chip
                  key={provider}
                  label={provider}
                  size="small"
                  variant="outlined"
                  color="primary"
                />
              ))}
            </Box>
          );
        },
      },
      {
        field: 'actions',
        headerName: 'Actions',
        width: 160,
        sortable: false,
        filterable: false,
        renderCell: (params: GridRenderCellParams<Entity>) => (
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', height: '100%' }}>
            <IconButton size="small" onClick={() => setEditing(params.row)} color="primary">
              <Edit fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => {
                setSelectedEntity(params.row);
                setMainTab(1);
              }}
              color="info"
            >
              <History fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={() => deleteMut.mutate(params.row.ID)} color="error">
              <Delete fontSize="small" />
            </IconButton>
          </Box>
        ),
      },
    ],
    [deleteMut, users]
  );

  if (isError) {
    return (
      <Stack spacing={2}>
        <Typography variant="h5">Entities</Typography>
        <Alert severity="error">Failed to load entities: {(error as Error).message}</Alert>
      </Stack>
    );
  }

  const rows = Array.isArray(data) ? data : [data].filter(Boolean);

  return (
    <Stack spacing={2}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4">Entities Management</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => setCreateDialogOpen(true)}>
          New Entity
        </Button>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={mainTab} onChange={(_, newValue) => setMainTab(newValue)}>
          <Tab label="Entities Overview" />
          <Tab label="EUA Balance History" disabled={!selectedEntity} />
        </Tabs>
      </Box>

      {mainTab === 0 && (
        <DataGrid
          autoHeight
          rows={rows}
          columns={cols}
          getRowId={(r) => r.ID}
          loading={isLoading}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 25 },
            },
          }}
        />
      )}

      {mainTab === 1 && selectedEntity && (
        <Box>
          <Box sx={{ mb: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
            <Typography variant="h6">EUA Balance History - {selectedEntity.AccountName}</Typography>
            <Typography variant="body2" color="textSecondary">
              MOHA Account: {selectedEntity.MohaAccountNumber}
            </Typography>
          </Box>
          <EuaBalanceHistory
            currentBalance={selectedEntity.MohaEuaBalance}
            history={balanceHistory}
          />
        </Box>
      )}

      <Dialog open={!!editing} onClose={() => setEditing(null)} fullWidth maxWidth="sm">
        <DialogTitle>Edit Entity</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Autocomplete
              options={users}
              getOptionLabel={(user) => UserService.formatDisplayName(user)}
              value={users.find((user) => user.uid === form.UserId) || null}
              onChange={(_, newValue) => setForm((f) => ({ ...f, UserId: newValue?.uid || '' }))}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Assigned User"
                  helperText="Select a user to assign this entity to"
                />
              )}
              renderOption={(props, user) => (
                <Box component="li" {...props}>
                  <Box>
                    <Typography variant="body2">{UserService.formatDisplayName(user)}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {user.email}
                    </Typography>
                  </Box>
                </Box>
              )}
            />
            <TextField
              label="Account Name"
              value={form.AccountName ?? ''}
              onChange={(e) => setForm((f) => ({ ...f, AccountName: e.target.value }))}
            />
            <TextField
              label="MOHA Account Number"
              value={form.MohaAccountNumber ?? ''}
              onChange={(e) => setForm((f) => ({ ...f, MohaAccountNumber: e.target.value }))}
            />
            <TextField
              label="EUA Balance"
              type="number"
              value={form.MohaEuaBalance ?? ''}
              onChange={(e) => setForm((f) => ({ ...f, MohaEuaBalance: Number(e.target.value) }))}
              helperText="Changes will be logged in balance history"
            />
            <TextField
              label="Trader Name"
              value={form.TraderName ?? ''}
              onChange={(e) => setForm((f) => ({ ...f, TraderName: e.target.value }))}
              helperText="Name of the assigned trader for EUA procurement"
            />
            <TextField
              label="Trader Email"
              type="email"
              value={form.TraderEmail ?? ''}
              onChange={(e) => {
                const email = e.target.value;
                setForm((f) => ({ ...f, TraderEmail: email }));
                setEmailError(validateEmail(email));
              }}
              error={!!emailError}
              helperText={emailError || 'Email address where EUA procurement requests will be sent'}
            />
            <TextField
              label="Report Email"
              type="email"
              value={form.ReportEmail ?? ''}
              onChange={(e) => {
                const email = e.target.value;
                setForm((f) => ({ ...f, ReportEmail: email }));
                setReportEmailError(validateEmails(email));
              }}
              error={!!reportEmailError}
              helperText={
                reportEmailError || 'Comma-separated email addresses for monthly emissions reports'
              }
            />

            {/* Data Providers Selection */}
            <Box sx={{ mt: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Select Data Providers</InputLabel>
                <Select
                  multiple
                  value={form.DataProviders || []}
                  label="Select Data Providers"
                  onChange={(e) =>
                    setForm((f) => ({ ...f, DataProviders: e.target.value as string[] }))
                  }
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {(selected as string[]).map((value) => (
                        <Chip key={value} label={value} size="small" />
                      ))}
                    </Box>
                  )}
                >
                  {availableProviders.map((provider) => (
                    <MenuItem key={provider} value={provider}>
                      <Checkbox checked={(form.DataProviders || []).includes(provider)} />
                      <ListItemText primary={provider} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormHelperText>
                Select which data providers this entity can use for voyage data
              </FormHelperText>
            </Box>

            {/* FuelEU-on-reports preference (default ON) */}
            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.FuelEUOnReports ?? true}
                    onChange={(e) => setForm((f) => ({ ...f, FuelEUOnReports: e.target.checked }))}
                  />
                }
                label="Show FuelEU figures on reports"
              />
              <FormHelperText>
                When enabled, this account's monthly and manual voyage reports include a FuelEU
                Maritime compliance summary (hidden automatically when a report has no FuelEU data)
              </FormHelperText>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setEditing(null);
              setEmailError('');
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => editing && updateMut.mutate({ id: editing.ID, payload: form })}
            disabled={!!emailError || !!reportEmailError}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Entity Dialog */}
      <CreateEntityDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onCreate={handleCreateEntity}
      />
    </Stack>
  );
}
